// Libraries
import { Flex, Skeleton, Text } from '@chakra-ui/react';
// Components
import { useDebouncedState } from '@hooks/index';
import { CustomNumberInput } from '@molecules/index';
// Utils
import text from '@translations/en.json';
// Types
import { FuelInputProps } from './FuelInput.types';

/**
 * `FuelInput` component is used for displaying and editing fuel-related input.
 * It includes a label, fuel category information, and an input for the fuel value.
 * Additionally, it supports optional features like loading state, error handling, and custom styling.
 *
 * @component
 * @param {FuelInputProps} props - The properties to configure the FuelInput component.
 * @param {boolean} props.isLoading - A flag indicating whether the component is in a loading state.
 * @param {string} props.label - The label for the input field.
 * @param {string} props.error - An error message to display, if any.
 * @param {string} props.name - The name of the fuel input.
 * @param {string} props.displayName - The display name of the fuel input, shown in parentheses next to the name.
 * @param {string} props.category - The category information for the fuel.
 * @param {number} props.inputValue - The current value of the fuel input.
 * @param {Function} props.onInputChange - A callback function triggered on input value change.
 * @param {Object} props.rest - Additional properties from chakra's NumberInputProps.
 * @returns {ReactElement} The rendered FuelInput component.
 */
export const FuelInput = ({
  isLoading,
  label,
  error,
  name,
  displayName,
  category,
  inputValue,
  onInputChange,
  ...rest
}: FuelInputProps) => {
  const [internalValue, setInternalValue] = useDebouncedState(inputValue, 100, onInputChange);
  const fuelName = `${name} ${displayName ? `(${displayName})` : ''}`;
  const pb = error ? 0 : 3;
  const mt = error ? -5 : -2;

  return (
    <Flex w='full' minW={60} gap={2} pt={3.5} pb={pb}>
      <Skeleton flex='1' isLoaded={!isLoading}>
        <Text fontSize='m' color='text.dark' lineHeight='base'>
          {fuelName}
        </Text>
        <Text fontSize='s' color='grey.100'>
          {`${category} ${text.fuel}`}
        </Text>
      </Skeleton>
      <Text fontSize='s' color='grey.100' alignSelf='center' mt={mt}>
        {label}
      </Text>
      <Flex direction='column' gap={1}>
        <Skeleton isLoaded={!isLoading}>
          <CustomNumberInput
            isInvalid={!!error}
            value={internalValue}
            setValue={setInternalValue}
            width={21}
            {...rest}
          />
          {!!error && (
            <Text h={3.5} mb={1} mt='1px' color='red'>
              {error}
            </Text>
          )}
        </Skeleton>
      </Flex>
    </Flex>
  );
};
